import { PageHeader } from "@/app/page-header";
import { Panel } from "@/components/ui/panel";
import { NotConnectedState } from "@/components/ui/states";

/**
 * The six modules that exist in the product but have no backend behind them.
 *
 * These deliberately ship empty. The original HTML preview filled them with
 * invented employees, candidates and scores; reproducing that here would make
 * the app look finished while showing numbers nobody can act on. Each screen
 * instead states plainly what it needs before it can show anything, which is
 * also the spec for whoever builds the API next.
 */
function AwaitingBackendPage({
  eyebrow,
  title,
  sub,
  module,
  what,
  needs,
}: {
  eyebrow: string;
  title: string;
  sub: string;
  module: string;
  what: string;
  needs: string[];
}) {
  return (
    <div className="flex flex-col gap-6">
      <PageHeader eyebrow={eyebrow} title={title} sub={sub} />
      <Panel>
        <NotConnectedState module={module} what={what} needs={needs} />
      </Panel>
    </div>
  );
}

export function HrPerformancePage() {
  return (
    <AwaitingBackendPage
      eyebrow="Org-wide"
      title="Performance & KRA/KPI"
      sub="Competencies, KRAs and KPIs per person, with targets and actuals feeding an automatic evaluation that flows through to the Manager and Employee accounts."
      module="Performance"
      what="Competencies, targets and actuals are not stored anywhere yet, so there is no real evaluation to show. Nothing here is populated with sample scores on purpose."
      needs={[
        "Tables for competencies, KRA/KPI definitions and per-period targets",
        "An actuals entry endpoint the employee writes to each month",
        "The evaluation rule that turns targets plus actuals into a rating",
        "Read endpoints scoped three ways: org-wide, team, and self",
      ]}
    />
  );
}

export function HrLifecyclePage() {
  return (
    <AwaitingBackendPage
      eyebrow="Org-wide"
      title="Employee lifecycle"
      sub="Stage tracking from pre-joining through exit, with a separate path per employment type."
      module="Lifecycle"
      what="The users table records an employment type and a joining date, but no stage history, so a lifecycle position cannot be derived without inventing it."
      needs={[
        "A stage definition per employment type: Full-time, Intern, Freelancer",
        "A per-person stage history with timestamps and who moved it",
        "Exit and offboarding fields, which the schema has no column for today",
      ]}
    />
  );
}

export function HrRecruitmentPage() {
  return (
    <AwaitingBackendPage
      eyebrow="Org-wide"
      title="Recruitment & hiring"
      sub="Open roles, their funnels, and every candidate's current stage, owned by HR with per-role visibility that can be opened to a hiring manager."
      module="Recruitment"
      what="There are no roles or candidates in the database. A funnel drawn from placeholder candidates would be indistinguishable from a real one, which is exactly the risk worth avoiding on a hiring screen."
      needs={[
        "Roles, candidates and application-stage tables",
        "Stage transition endpoints, so a funnel can be counted rather than guessed",
        "A per-role visibility grant, for opening a pipeline to one manager",
      ]}
    />
  );
}

export function HrHiringDashboardPage() {
  return (
    <AwaitingBackendPage
      eyebrow="Admin Account only"
      title="Hiring Dashboard"
      sub="Open positions, pipeline, background verification, induction, onboarding index and quality of hire, in one place."
      module="Hiring Dashboard"
      what="This reads across recruitment, background verification and induction records, none of which exist as API resources yet. It stays empty until they do."
      needs={[
        "The recruitment tables above, as its primary source",
        "Background verification records per hire",
        "Induction and onboarding checklists, separate from the account-setup flow already built",
        "A quality-of-hire measure, which needs the performance module first",
      ]}
    />
  );
}

export function HrSentimentPage() {
  return (
    <AwaitingBackendPage
      eyebrow="Admin Account only"
      title="Relationship & sentiment tracking"
      sub="The HRBP one-on-one structure: master tracker, conversation log, leadership report and escalation log."
      module="Sentiment tracking"
      what="Conversation notes are confidential HR records and there is no store for them. Sample sentiment entries against real colleagues' names would be worse than an empty screen, so this shows nothing until the store exists."
      needs={[
        "A conversation log table with per-entry access control",
        "Sentiment dimensions and a monthly theme, per the HRBP tracker",
        "An escalation log with tiering, kept separate from the general log",
        "A grant mechanism, since this module is opened to a manager case by case",
      ]}
    />
  );
}

export function HrAccessGrantsPage() {
  return (
    <AwaitingBackendPage
      eyebrow="Admin Account only"
      title="Access grants"
      sub="Situational access opened beyond the default role permissions, auditable and revocable at any time."
      module="Access grants"
      what="Permissions today come entirely from the role baked into each session token. There is no grant table, so there is nothing to list, and an empty list is the accurate answer rather than a bug."
      needs={[
        "A grants table: who, which module, which scope, granted by, expires when",
        "Middleware that checks grants alongside the role on every gated route",
        "An audit trail of grants and revocations",
      ]}
    />
  );
}
